import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CreditCardHolder } from "@/backend-sdk/client/types.gen";
import { applyCEPMask, applyPhoneMask } from "@/components/checkout/masks";

/**
 * O que o gateway exige do titular do cartão e o cartão não traz: o CEP, o número do
 * endereço e o telefone. Sem os três a cobrança é recusada (M-027).
 *
 * Fica junto do cartão, e não é lido do cadastro, porque é do **titular** — que pode não
 * ser quem assina. O telefone é o único opcional: em branco, o servidor usa o do cadastro.
 */
export interface CardHolderFormState {
  postalCode: string;
  addressNumber: string;
  phone: string;
}

export const EMPTY_CARD_HOLDER: CardHolderFormState = {
  postalCode: "",
  addressNumber: "",
  phone: "",
};

interface CardHolderFormProps {
  value: CardHolderFormState;
  onChange: (value: CardHolderFormState) => void;
}

export const CardHolderForm = ({ value, onChange }: CardHolderFormProps) => {
  const set = (patch: Partial<CardHolderFormState>) =>
    onChange({ ...value, ...patch });

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <Label htmlFor="holder-cep">CEP do titular</Label>
          <Input
            id="holder-cep"
            inputMode="numeric"
            value={value.postalCode}
            onChange={(e) => set({ postalCode: applyCEPMask(e.target.value) })}
            placeholder="00000-000"
          />
        </div>
        <div>
          <Label htmlFor="holder-number">Número</Label>
          <Input
            id="holder-number"
            maxLength={20}
            value={value.addressNumber}
            onChange={(e) => set({ addressNumber: e.target.value })}
            placeholder="123"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="holder-phone">Telefone do titular (opcional)</Label>
        <Input
          id="holder-phone"
          inputMode="tel"
          value={value.phone}
          onChange={(e) => set({ phone: applyPhoneMask(e.target.value) })}
          placeholder="(00) 00000-0000"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Em branco, usamos o telefone do seu cadastro.
        </p>
      </div>
    </div>
  );
};

export const isCardHolderComplete = (holder: CardHolderFormState): boolean =>
  holder.postalCode.replace(/\D/g, "").length === 8 &&
  holder.addressNumber.trim() !== "";

/**
 * Em branco ou com DDD (10 ou 11 dígitos). Um telefone pela metade não é "sem telefone":
 * ele iria no lugar do do cadastro, e o gateway recusaria a cobrança.
 */
export const isCardHolderPhoneValid = (holder: CardHolderFormState): boolean => {
  const digits = holder.phone.replace(/\D/g, "").length;
  return digits === 0 || digits === 10 || digits === 11;
};

export const toCreditCardHolder = (
  holder: CardHolderFormState,
): CreditCardHolder => ({
  postal_code: holder.postalCode.replace(/\D/g, ""),
  address_number: holder.addressNumber.trim(),
  phone: holder.phone.replace(/\D/g, "") || null,
});
